import { z } from 'zod';

/**
 * A send that a workflow queued for later: a WhatsApp/SMS message or an
 * outbound call. Content is resolved when the workflow runs and stored
 * verbatim, so `rendered_body` is literally what the recipient will read.
 */
export const scheduledMessageChannelSchema = z.enum(['WHATSAPP', 'SMS', 'CALL']);
export type ScheduledMessageChannel = z.infer<typeof scheduledMessageChannelSchema>;

export const scheduledMessageStatusSchema = z.enum([
  'pending',
  // Claimed by a dispatcher and in flight — the claim is what stops the
  // safety-net sweep from double-sending a row the delayed job already took.
  'sending',
  'sent',
  'failed',
  'cancelled',
]);
export type ScheduledMessageStatus = z.infer<typeof scheduledMessageStatusSchema>;

export const SCHEDULED_MESSAGE_CHANNELS = scheduledMessageChannelSchema.options;
export const SCHEDULED_MESSAGE_STATUSES = scheduledMessageStatusSchema.options;

export const SCHEDULED_MESSAGE_CHANNEL_LABELS: Record<ScheduledMessageChannel, string> = {
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  CALL: 'Call',
};

export const SCHEDULED_MESSAGE_STATUS_LABELS: Record<ScheduledMessageStatus, string> = {
  pending: 'Pending',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** Ordered WhatsApp template parameters, by component. */
export const scheduledTemplateParamsSchema = z.object({
  header: z.array(z.string()).optional(),
  body: z.array(z.string()).optional(),
  button: z.array(z.string()).optional(),
});

export type ScheduledTemplateParams = z.infer<typeof scheduledTemplateParamsSchema>;

export const scheduledMessageSchema = z.object({
  id: z.string().uuid(),
  channel: scheduledMessageChannelSchema,
  status: scheduledMessageStatusSchema,
  scheduled_for: z.string(),

  to_address: z.string(),
  from_address: z.string().nullable().optional(),

  template_name: z.string().nullable().optional(),
  template_language: z.string().nullable().optional(),
  template_params: scheduledTemplateParamsSchema.nullable().optional(),
  body: z.string().nullable().optional(),
  rendered_body: z.string().nullable().optional(),
  provider: z.string().nullable().optional(),

  configuration_id: z.string().uuid().nullable().optional(),
  contact_id: z.string().uuid().nullable().optional(),
  call_id: z.string().uuid().nullable().optional(),
  booking_id: z.string().uuid().nullable().optional(),

  workflow_id: z.string().uuid().nullable().optional(),
  node_id: z.string().nullable().optional(),

  sent_at: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  provider_message_id: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});

export type ScheduledMessage = z.infer<typeof scheduledMessageSchema>;

export const scheduledMessageWithRelationsSchema = scheduledMessageSchema.extend({
  contact: z
    .object({
      id: z.string().uuid(),
      name: z.string().nullable(),
      phone: z.string().nullable(),
    })
    .nullable()
    .optional(),
  configuration: z.object({ id: z.string().uuid(), name: z.string() }).nullable().optional(),
  booking: z
    .object({ id: z.string().uuid(), title: z.string(), date: z.string(), status: z.string() })
    .nullable()
    .optional(),
});

export type ScheduledMessageWithRelations = z.infer<typeof scheduledMessageWithRelationsSchema>;

export const scheduledMessageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
  channel: scheduledMessageChannelSchema.optional(),
  status: scheduledMessageStatusSchema.optional(),
  contactId: z.string().uuid().optional(),
  bookingId: z.string().uuid().optional(),
  search: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

export type ScheduledMessageQuery = z.infer<typeof scheduledMessageQuerySchema>;
